import { DialogBody } from '@civilgrid/ui/components';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS, type UtilityContactsSource } from '~/common/posthogEvents';
import { TableV2 } from '~/components2/Table';
import { getUtilityContacts } from '~/pages/projectDetails/utilitiesV2/UtilityContacts/utility';
import type { UtilityContact } from '../common';
import store from '../store';
import { columns } from './columns';

export default function UtilityContactsComponent({ source }: { source: UtilityContactsSource }) {
  const { project, agencies } = useSnapshot(store) as typeof store;

  // Convert from agencies to contacts for table display
  const contacts = useMemo(() => getUtilityContacts(agencies), [agencies]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once when the project id is known
  useEffect(() => {
    if (project?.id) {
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchUtilityContactsViewed, {
        project_id: project.id,
        source,
      });
    }
  }, [project?.id]);

  return (
    <DialogBody
      className="flex min-h-48 max-h-[70vh] w-[80vw] max-w-7xl flex-none flex-col overflow-y-clip"
      data-testid="utilities-contacts-dialog-body"
    >
      <Content contacts={contacts} />
    </DialogBody>
  );
}

interface ContentProps {
  contacts: UtilityContact[];
}

const Content = ({ contacts }: ContentProps) => {
  const table = useReactTable({
    data: contacts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    initialState: {
      sorting: [{ id: 'agencyName', desc: false }],
    },
  });

  return (
    <div className="w-full min-h-0 grow overflow-y-auto">
      <div className="max-w-full text-sm">
        <TableV2 id="utilities-contacts-v1" table={table} />
      </div>
    </div>
  );
};
